import { Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { useStoreBvHistory, useStoreId } from "@/hooks/queries";
import type { StoreBvLedgerDto } from "@/types/store.dto";

const entryBadge: Record<string, string> = {
  AdminCredit: "bg-success/10 text-success border-success/20",
  AdminDebit: "bg-destructive/10 text-destructive border-destructive/20",
  ProductAllocate: "bg-warning/10 text-warning border-warning/20",
  ProductRefund: "bg-info/10 text-info border-info/20",
};

const StoreReceiveBv = () => {
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data, isLoading } = useStoreBvHistory(storeId);

  const balance = data?.balance;
  const entries = data?.entries ?? [];

  const table = useDataTable<StoreBvLedgerDto>({
    data: entries,
    searchKeys: ["description", "productName", "entryTypeText"],
  });

  const formatBv = (value: number) =>
    Number.isInteger(value) ? String(value) : Number(value).toFixed(2);

  const columns: DataTableColumn<StoreBvLedgerDto>[] = [
    {
      id: "date",
      header: "Date",
      sortable: true,
      accessorKey: "createdAt",
      mobilePriority: 1,
      cell: (row) =>
        new Date(row.createdAt).toLocaleString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      id: "type",
      header: "Type",
      mobileLabel: "Type",
      mobilePriority: 2,
      cell: (row) => (
        <Badge variant="outline" className={entryBadge[row.entryTypeText] ?? ""}>
          {row.entryTypeText.replace(/([a-z])([A-Z])/g, "$1 $2")}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: "BV",
      align: "right",
      mobileLabel: "BV",
      mobilePriority: 3,
      cell: (row) => {
        const isCredit =
          row.entryTypeText === "AdminCredit" || row.entryTypeText === "ProductRefund";
        return (
          <span className={isCredit ? "font-semibold text-success" : "font-semibold text-destructive"}>
            {isCredit ? "+" : "-"}
            {formatBv(Number(row.amount))}
          </span>
        );
      },
    },
    {
      id: "balance",
      header: "Balance after",
      align: "right",
      mobileLabel: "Balance",
      mobilePriority: 4,
      cell: (row) => formatBv(Number(row.balanceAfter)),
    },
    {
      id: "description",
      header: "Details",
      mobileLabel: "Details",
      mobilePriority: 5,
      cell: (row) => (
        <div className="text-sm">
          <p>{row.description}</p>
          {row.notes ? <p className="text-xs text-muted-foreground">{row.notes}</p> : null}
          {row.productName ? (
            <p className="text-xs text-muted-foreground">Product: {row.productName}</p>
          ) : null}
        </div>
      ),
    },
  ];

  if (storeLoading || isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-28 rounded-xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Receive BV"
        description="BV credited by admin. Use available BV when adding or increasing product B.V."
      />

      <StatsGrid>
        <CompactStatCard title="Available BV" value={formatBv(Number(balance?.availableBv ?? 0))} icon={Layers} />
        <CompactStatCard title="Total received" value={formatBv(Number(balance?.totalBvReceived ?? 0))} icon={Layers} />
        <CompactStatCard title="Allocated to products" value={formatBv(Number(balance?.totalBvAllocated ?? 0))} icon={Layers} />
      </StatsGrid>

      {(balance?.availableBv ?? 0) <= 0 ? (
        <p className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-sm text-warning">
          Your BV balance is zero. You cannot add products or increase product BV until admin credits BV.
        </p>
      ) : null}

      <DataTable
        title="BV history"
        columns={columns}
        data={table.rows}
        getRowId={(row) => String(row.id)}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search BV history..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No BV credits yet. Ask admin to add BV to your store."
      />
    </div>
  );
};

export default StoreReceiveBv;
